import { useFaireDirectBrand } from "@faire/deprecated-retailer-visitor-shared/hooks/FaireDirect/useFaireDirectBrand/useFaireDirectBrand";
import * as RetailerPaths from "@faire/deprecated-retailer-visitor-shared/paths/index";
import { Action, Core } from "@faire/slate/foundation/color";
import { trackRetailerPortalBreadcrumbHomeView } from "@faire/web-api--source/events/retailerPortal/view/breadcrumbHome";
import { useStrictLocalization } from "@faire/web/common/localization";
import { faireBaseUrl } from "@faire/web/common/routes/faireBaseUrl";
import { trackEvent } from "@faire/web/common/trackEvent";
import { Button } from "@faire/web/slate-deprecated/Button";
import { Row } from "@faire/web/slate/Layout";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { Anchor } from "@faire/web/ui/LinkAnchor";
import * as React from "react";
import { useEffect, useState } from "react";
import styled from "styled-components";

export interface IBreadcrumbOption {
  label: string;
  path?: string;
  eventId?: string;
}

interface IProps {
  className?: string;
  options: IBreadcrumbOption[];
  verticalPadding?: string;
  horizontalPadding?: string;
  hideRoot?: boolean;
  dataTestId?: string;
  onClick?: (option: IBreadcrumbOption) => void;

  /**
   * Allows to use a custom Button component for the navigation element.
   * @default slate/Button
   */
  element?: React.FC<React.ComponentProps<typeof Button>>;
}

/**
 * This should be moved to @retailer. It depends on it, and is only used there
 * @trackcomponent
 * @supersededBy "@faire/web/ui/Breadcrumbs/Breadcrumbs#Breadcrumbs"
 */
export const Breadcrumb: React.FC<IProps> = ({
  element: NavigationElement = Button,
  ...props
}) => {
  const { strictLocalize } = useStrictLocalization();

  const {
    className,
    verticalPadding,
    horizontalPadding,
    hideRoot,
    dataTestId,
    onClick,
  } = props;

  const [options, setOptions] = useState<IBreadcrumbOption[]>(props.options);

  const { isFaireDirectActiveInLoggedOut } = useFaireDirectBrand();

  useEffect(() => {
    setOptions(props.options);
  }, [props.options]);

  const handleClick = (index: number, option: IBreadcrumbOption) => {
    onClick?.(option);

    setOptions(options.slice(0, index + 1));

    setOptions((prevOptions) => {
      return prevOptions.map((opt, i) => {
        if (i === index) {
          return { ...opt, path: undefined };
        }
        return opt;
      });
    });

    if (option.eventId) {
      trackEvent(`breadcrumb/link/click/${option.eventId}`, true);
    }
  };

  const homeMessage = strictLocalize({
    id: "oGOLQY",
    defaultMessage: "Home",
    description: { text: "Link to the homepage" },
  });

  return (
    <BreadcrumbRow
      className={className}
      $verticalPadding={verticalPadding}
      $horizontalPadding={horizontalPadding}
      data-test-id={dataTestId}
      role="navigation"
      aria-label={strictLocalize({
        id: "2ezgqJ",
        defaultMessage: "Breadcrumbs",
        description: {
          text: "Label of the breadcrumbs for navigation",
        },
      })}
    >
      <StyledList>
        {!hideRoot ? (
          <li>
            {isFaireDirectActiveInLoggedOut ? (
              <Anchor
                href={faireBaseUrl()}
                onClick={() => {
                  trackRetailerPortalBreadcrumbHomeView();
                }}
              >
                <Typography>
                  <LinkText>{homeMessage}</LinkText>
                  {`  /  `}
                </Typography>
              </Anchor>
            ) : (
              <NavigationElement
                style={{ textDecoration: "none" }}
                variant="tertiary"
                linkTo={RetailerPaths.home()}
                onClick={() => {
                  trackRetailerPortalBreadcrumbHomeView();
                }}
              >
                <Typography>
                  <LinkText>{homeMessage}</LinkText>
                  {`  /  `}
                </Typography>
              </NavigationElement>
            )}
          </li>
        ) : null}
        {options.map((option: IBreadcrumbOption, index: number) =>
          option.path ? (
            <li key={index}>
              <NavigationElement
                style={{ textDecoration: "none" }}
                variant="tertiary"
                linkTo={option.path}
                onClick={(e) => {
                  // We don't want to trim the list if we open in a new tab
                  if (e.ctrlKey || e.metaKey) {
                    return;
                  }
                  handleClick(index, option);
                }}
              >
                <Typography>
                  <LinkText>{option.label}</LinkText>
                  {index !== options.length - 1 ? "  /  " : ""}
                </Typography>
              </NavigationElement>
            </li>
          ) : (
            <li key={index}>
              <Typography color={Core.text.subdued}>
                <span>{option.label}</span>
                {index !== options.length - 1 ? "  /  " : ""}
              </Typography>
            </li>
          )
        )}
      </StyledList>
    </BreadcrumbRow>
  );
};

const BreadcrumbRow = styled(Row).attrs({ align: "flex-start" })<{
  $verticalPadding?: string;
  $horizontalPadding?: string;
}>`
  &&& {
    display: block;
  }
  line-height: 16px;
  max-width: 1700px;
  margin: auto;
  width: 100%;
  ${responsiveMediaQueries.mobile} {
    padding: ${(props) => props.$verticalPadding || "12px"}
      ${(props) => props.$horizontalPadding || "16px"};
  }

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${(props) => props.$verticalPadding || "12px"}
      ${(props) => props.$horizontalPadding || "24px"};
  }
`;

const LinkText = styled.span`
  &:hover {
    border-bottom: 1px solid ${Action.border.default};
  }
`;

const StyledList = styled.ol`
  list-style-type: none;
  li {
    display: inline-block;
    padding-right: 4px;
  }
`;
